<template>
  <v-card class="pa-6" flat>
    <v-form ref="form" v-model="valid">
      <!-- Title Section -->
      <h3 class="title mb-6">Edit Property</h3>
      
      <!-- Search Section -->
      <v-row>
        <v-col cols="12" md="8">
          <div class="text-subtitle-1 black--text mb-2">Search for property to edit</div>
          <Search 
            label="Search by address"
            @selectedOID="fetchProperty" 
            @clearItem="handleClearSearch"
            endpoint="property"
            myParams="address"
          />
        </v-col>
      </v-row>

      <!-- Selected Property Card -->
      <v-card v-if="selectedPropertyID && !fetchingProperty" class="mt-4 mb-6" outlined>
        <v-card-text>
          <div class="text-subtitle-2 mb-1">Currently Editing:</div>
          <div class="text-body-1">{{ displayAddress }}</div>
          <div class="text-caption">Property ID: {{ selectedPropertyID }}</div>
        </v-card-text>
      </v-card>

      <!-- Loading Progress -->
      <v-progress-linear v-if="fetchingProperty" indeterminate color="secondary" class="mb-4"></v-progress-linear>

      <!-- Property Details Form -->
      <div v-if="selectedPropertyID">
        <v-divider class="mb-6"></v-divider>
        
        <h4 class="subtitle-1 mb-4">Property Details</h4>
        
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="houseNumber"
              label="House Name/Number"
              outlined
              dense
              :disabled="fetchingProperty"
            />
          </v-col>
          <v-col cols="12" md="9">
            <v-text-field
              v-model="address1"
              label="Address Line 1"
              outlined
              dense
              :disabled="fetchingProperty"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="address2"
              label="Address Line 2"
              outlined
              dense
              :disabled="fetchingProperty"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="address3"
              label="Address Line 3"
              outlined
              dense
              :disabled="fetchingProperty"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="address4"
              label="Address Line 4"
              outlined
              dense
              :disabled="fetchingProperty"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="postcode"
              label="Postcode"
              outlined
              dense
              :disabled="fetchingProperty"
              :rules="[rules.required]"
            />
          </v-col>
        </v-row>
        
        <!-- Action Buttons -->
        <v-row class="mt-6">
          <v-col cols="12" class="text-right">
            <v-btn
              text
              @click="handleClearSearch"
              :disabled="loading"
              class="mr-3"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              :loading="loading"
              :disabled="!valid || fetchingProperty"
              @click="saveProperty"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-8">
        <v-icon size="48" color="grey lighten-2">mdi-home-search</v-icon>
        <p class="text-subtitle-1 grey--text mt-3">Search for a property above to start editing</p>
      </div>
    </v-form>

    <!-- Snackbar for notifications -->
    <Snackbar 
      :snackbarDialog="snackbar"
      :snackbarText="snackbarText"
      :timeout="timeout"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
    />
  </v-card>
</template>

<script>
import { amplifyProxy } from '@/services/directApi'
import Snackbar from '@/components/Snackbars/TheSnackbar'
import Search from '@/components/CloudSearch/Search'

export default {
  components: {
    Snackbar,
    Search
  },
  data() {
    return {
      valid: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      loading: false,
      fetchingProperty: false,
      selectedPropertyID: null,
      houseNumber: '',
      address1: '',
      address2: '',
      address3: '',
      address4: '',
      postcode: '',
      rules: {
        required: value => !!value || 'Required field',
      }
    }
  },
  computed: {
    displayAddress() {
      const parts = [
        this.houseNumber,
        this.address1,
        this.address2,
        this.address3,
        this.address4,
        this.postcode
      ].filter(Boolean)
      return parts.join(', ') || 'No address available'
    }
  },
  methods: {
    closeSnackbar() {
      this.snackbar = false
    },

    clearForm() {
      this.houseNumber = ''
      this.address1 = ''
      this.address2 = ''
      this.address3 = ''
      this.address4 = ''
      this.postcode = ''
    },

    handleClearSearch() {
      this.selectedPropertyID = null
      this.clearForm()
    },

    async fetchProperty(id) {
      this.selectedPropertyID = id
      this.clearForm() // Clear form before fetching new data
      this.fetchingProperty = true

      try {
        const endpoint = `/${this.$store.state.user.ClientName}/properties/${id}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.data

        // Handle the property data structure
        this.houseNumber = data.HouseNumber || ''
        this.address1 = data.Address?.Address1 || ''
        this.address2 = data.Address?.Address2 || ''
        this.address3 = data.Address?.Address3 || ''
        this.address4 = data.Address?.Address4 || ''
        this.postcode = data.Address?.Postcode || ''
      } catch (error) {
        console.error('Error fetching property:', error)
        this.snackbar = true
        this.snackbarText = 'Error fetching property details: ' + (error.response?.data?.message || error.message)
        this.snackbarColor = 'red'
      } finally {
        this.fetchingProperty = false
      }
    },

    async saveProperty() {
      this.loading = true

      const propertyObject = {
        HouseNumber: this.houseNumber,
        Address: {
          Address1: this.address1,
          Address2: this.address2,
          Address3: this.address3,
          Address4: this.address4,
          Postcode: this.postcode
        }
      }


      try {
        const endpoint = `/${this.$store.state.user.ClientName}/properties/${this.selectedPropertyID}`
        const options = {
          headers: {
            token: this.$store.state.user.LoginToken
          }
        }

        await amplifyProxy.patch(endpoint, propertyObject, options)

        this.snackbar = true
        this.snackbarText = 'Property update successful'
        this.snackbarColor = 'green'
        await this.fetchProperty(this.selectedPropertyID)
      } catch (error) {
        this.snackbar = true
        this.snackbarText = 'ERROR: ' + (error.response?.data || error.message)
        this.snackbarColor = 'red'
        await this.fetchProperty(this.selectedPropertyID)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>